import { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Pill, Leaf, Droplet, Clock, Trash2 } from 'lucide-react';
import { Icon } from '../ui/Icon';
import { fmtTime } from '../../lib/format';
import { useHoldToConfirm } from '../../lib/holdToConfirm';
import { EASE_OUT, useReducedMotion } from '../../lib/motion';
import type { Dose } from '@shared/types';

const FORM_ICON = {
  tablet_7oh: Pill,
  leaf: Leaf,
  extract: Droplet,
} as const;

// Swipe-left threshold past which the Delete affordance "snaps" open.
// Calibrated to feel deliberate, not accidental.
const SWIPE_THRESHOLD = 60;
const MAX_OFFSET = 80;

interface DoseChipProps {
  dose: Dose;
  onClick?: () => void;
  /** When provided, swipe-left reveals a Delete affordance. */
  onDelete?: () => void;
}

export function DoseChip({ dose, onClick, onDelete }: DoseChipProps) {
  const I = FORM_ICON[dose.form];
  const amount =
    dose.form === 'leaf' ? `${dose.amount} g` : `${dose.amount} ${dose.amountUnit}`;
  const reduce = useReducedMotion();

  const [offset, setOffset] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const startXRef = useRef<number | null>(null);
  const startedSwipeRef = useRef(false);

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (!onDelete) return;
    startXRef.current = e.clientX;
    startedSwipeRef.current = false;
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!onDelete || startXRef.current == null) return;
    const dx = e.clientX - startXRef.current;
    if (dx < -4) startedSwipeRef.current = true;
    if (startedSwipeRef.current) {
      const next = Math.max(-MAX_OFFSET, Math.min(0, dx));
      setOffset(next);
    }
  }

  function onPointerUp() {
    if (!onDelete) return;
    if (startedSwipeRef.current && offset < -SWIPE_THRESHOLD) {
      setOffset(-MAX_OFFSET);
      setRevealed(true);
    } else {
      setOffset(0);
      setRevealed(false);
    }
    startXRef.current = null;
    startedSwipeRef.current = false;
  }

  function handleChipClick() {
    if (revealed) {
      setOffset(0);
      setRevealed(false);
      return;
    }
    onClick?.();
  }

  const motionProps = reduce
    ? {}
    : {
        initial: { opacity: 0, y: 8, scale: 0.95 },
        animate: { opacity: 1, y: 0, scale: 1 },
        exit: { opacity: 0, x: -40, scale: 0.95 },
        transition: { duration: 0.18, ease: EASE_OUT },
      };

  if (!onDelete) {
    return (
      <motion.div layout {...motionProps} style={{ display: 'inline-block' }}>
        <button type="button" className="dose-chip" onClick={onClick}>
          <Icon icon={I} size={16} />
          <span className="dose-chip-amount">{amount}</span>
          <Icon icon={Clock} size={14} />
          <span className="dim">{fmtTime(dose.ts)}</span>
        </button>
      </motion.div>
    );
  }

  function handleTrashClick(e: React.MouseEvent<HTMLButtonElement>) {
    // Stop the pointer chain so the swipe machinery on the body
    // doesn't interpret this tap as a swipe-cancel; also keep the
    // chip's primary onClick from firing through the wrapper.
    e.stopPropagation();
    onDelete?.();
  }

  return (
    <motion.div
      layout
      {...motionProps}
      className="dose-chip-swipe"
    >
      <button
        type="button"
        aria-label={`Delete ${amount} dose at ${fmtTime(dose.ts)}`}
        onClick={onDelete}
        className="dose-chip-delete"
      >
        <Icon icon={Trash2} size={14} />
        Delete
      </button>
      <div
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className="dose-chip-swipe-body"
        style={{
          transform: `translateX(${offset}px)`,
          transition: startedSwipeRef.current
            ? 'none'
            : 'transform var(--dur-fast) var(--ease-out)',
        }}
      >
        <button type="button" className="dose-chip" onClick={handleChipClick}>
          <Icon icon={I} size={16} />
          <span className="dose-chip-amount">{amount}</span>
          <Icon icon={Clock} size={14} />
          <span className="dim">{fmtTime(dose.ts)}</span>
          {/* Visible delete affordance — the swipe gesture stays for
              power users, but the icon makes delete discoverable for
              everyone else. stopPropagation so a tap here doesn't also
              fire the chip's onClick or the swipe handlers below. */}
          <span
            role="button"
            tabIndex={0}
            aria-label={`Delete ${amount} dose at ${fmtTime(dose.ts)}`}
            className="dose-chip-trash"
            onClick={(e) => handleTrashClick(e as unknown as React.MouseEvent<HTMLButtonElement>)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onDelete?.();
              }
            }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <Icon icon={Trash2} size={14} />
          </span>
        </button>
      </div>
    </motion.div>
  );
}

interface DosePresetChipProps {
  label: string;
  onClick: () => void;
  /**
   * When true, the chip uses tap-and-hold-to-confirm instead of immediate
   * tap. The user must hold for ~600ms (a copper fill animates from
   * left to right) before `onClick` fires. Used on the TodayScreen
   * quick-log strip to add a deliberate ritual to dose logging without
   * losing the speed of a chip surface.
   *
   * Default: false (legacy tap-to-fire behavior preserved).
   */
  holdToConfirm?: boolean;
}

export function DosePresetChip({ label, onClick, holdToConfirm = false }: DosePresetChipProps) {
  if (!holdToConfirm) {
    return (
      <button type="button" className="dose-chip" onClick={onClick}>
        <span className="dose-chip-amount">{label}</span>
      </button>
    );
  }
  return <DosePresetChipHold label={label} onConfirm={onClick} />;
}

// Internal — uses the useHoldToConfirm hook. Separated so the non-hold
// path stays a plain stateless button.
function DosePresetChipHold({ label, onConfirm }: { label: string; onConfirm: () => void }) {
  const { bind, progress, holding } = useHoldToConfirm({ durationMs: 600, onConfirm });
  return (
    <button
      type="button"
      className="dose-chip dose-chip-preset"
      data-holding={holding ? 'true' : undefined}
      style={{ ['--hold-progress' as string]: String(progress) }}
      {...bind}
    >
      <span className="dose-chip-amount">{label}</span>
    </button>
  );
}
